import { getEnv } from '@/utils/env';

export type Params = {
  url: string;
  body?: Record<string, unknown> | Record<string, unknown>[];
  headers?: HeadersInit;
  ignoreResponse?: boolean;
  includeCredentials?: boolean;
  host?: string;
};

export class FetchError extends Error {
  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public body: any,
    public response: Response
  ) {
    super(response.statusText);
  }
}

const handleJsonResponse = async <T>(res: Response): Promise<T> => {
  if (res.headers.get('Content-Type')?.includes('application/json')) {
    const json = (await res.json()) as T;

    if (!res.ok) {
      throw new FetchError(json, res);
    }

    return json;
  } else {
    const text = await res.text();
    throw new FetchError(text, res);
  }
};

const handleOkResponse = async (res: Response): Promise<boolean> => {
  return res.ok;
};

const getHeaders = (headers: HeadersInit = {}): Headers => {
  // AT FIRST will support the bl-token header for authentication
  // just to get this off the ground
  // will soon replace with Authorization Bearer token
  const _headers = new Headers(headers);
  const storedToken = localStorage.getItem('token');

  if (storedToken) {
    _headers.set('BL-TOKEN', storedToken);
  }

  _headers.set('Content-Type', 'application/json');
  return _headers;
};

const SendRequestWithBody = async <T>(
  method: string,
  { url, headers, body, ignoreResponse, includeCredentials = false, host }: Params
): Promise<T> => {
  host ??= getEnv().VITE_API_HOST;
  try {
    const res = await fetch(`${host}${url}`, {
      method: method,
      headers: getHeaders(headers),
      body: JSON.stringify(body),
      credentials: includeCredentials ? 'include' : undefined,
    });

    if (ignoreResponse) {
      return (await handleOkResponse(res)) as T;
    }

    return await handleJsonResponse<T>(res);
  } catch (e: unknown) {
    if (e instanceof FetchError) {
      throw e;
    }

    throw new FetchError(e, new Response(null, { status: 500 }));
  }
};

const getUrlFromPath = (path: string): string => {
  if (!path.startsWith('http')) {
    return `${getEnv().VITE_API_HOST}${path}`;
  } else {
    return path;
  }
};

export const Get = async <T>({ url, headers, ignoreResponse, includeCredentials }: Params): Promise<T> => {
  try {
    const res = await fetch(getUrlFromPath(url), {
      method: 'GET',
      headers: getHeaders(headers),
      credentials: includeCredentials ? 'include' : undefined,
    });

    if (ignoreResponse) {
      return (await handleOkResponse(res)) as T;
    }

    return await handleJsonResponse<T>(res);
  } catch (e: unknown) {
    if (e instanceof FetchError) {
      throw e;
    }

    throw new FetchError(e, new Response(null, { status: 500 }));
  }
};

export const PostHv = async <T>({ url, headers, body, host }: Params): Promise<T> => {
  return SendRequestWithBody<T>('POST', { url, headers, body, host });
};

export const Put = async <T>({ url, headers, body, includeCredentials }: Params): Promise<T> => {
  return SendRequestWithBody<T>('PUT', { url, headers, body, includeCredentials });
};

export const Post = async <T>({ url, headers, body, ignoreResponse, includeCredentials }: Params): Promise<T> => {
  return SendRequestWithBody<T>('POST', { url, headers, body, ignoreResponse, includeCredentials });
};

export const Patch = async <T>({ url, headers, body }: Params): Promise<T> => {
  return SendRequestWithBody<T>('PATCH', { url, headers, body });
};

export const Delete = async <T>({ url, headers, body }: Params): Promise<T> => {
  return SendRequestWithBody<T>('DELETE', { url, headers, body });
};
